import { parse, RouteParseError } from '@noths/polaris-product-listings-urls';
import type { ParsedUrlQuery } from 'querystring';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { SyncProductsQueryFromURLPayload } from 'src/redux/productsQuery/types/ProductsQueryState';
import { PageType } from 'src/types/navigation';
import { getPageFromParam } from 'src/utils/queryParams';
import { shopSurfaceBasePath } from 'src/utils/shopSurfaceBasePath';

const ATTRIBUTES_SHORT_PREFIX = 'a.';
const ATTRIBUTES_FULL_PREFIX = 'attributes.';

const getFirstValue = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value;

const expandFacetKey = (shortKey: string): string =>
  shortKey.startsWith(ATTRIBUTES_SHORT_PREFIX)
    ? `${ATTRIBUTES_FULL_PREFIX}${shortKey.slice(ATTRIBUTES_SHORT_PREFIX.length)}`
    : shortKey;

const parseSortValue = (
  rawSort: string | undefined,
): { descending: boolean; key: string } | null => {
  if (!rawSort) {
    return null;
  }

  const lastDashIndex = rawSort.lastIndexOf('-');

  if (lastDashIndex === -1) {
    return { descending: false, key: expandFacetKey(rawSort) };
  }

  const direction = rawSort.slice(lastDashIndex + 1);

  if (direction !== 'asc' && direction !== 'desc') {
    return { descending: false, key: expandFacetKey(rawSort) };
  }

  return {
    descending: direction === 'desc',
    key: expandFacetKey(rawSort.slice(0, lastDashIndex)),
  };
};

const getQueryFromAsPath = (asPath: string): ParsedUrlQuery => {
  const queryStringIndex = asPath.indexOf('?');
  if (queryStringIndex === -1) {
    return {};
  }

  const queryString = asPath.slice(queryStringIndex + 1).split('#')[0];
  const params = new URLSearchParams(queryString);
  const query: ParsedUrlQuery = {};

  params.forEach((value, key) => {
    const existingValue = query[key];

    if (existingValue === undefined) {
      query[key] = value;
      return;
    }

    if (Array.isArray(existingValue)) {
      existingValue.push(value);
      return;
    }

    query[key] = [existingValue, value];
  });

  return query;
};

const stripQueryAndHash = (asPath: string): string => asPath.split('?')[0].split('#')[0];

/**
 * Builds the `syncFromURL` payload from a full URL + current Redux state.
 */
export function adaptPageURLToStore(
  asPath: string,
  state: Pick<ReduxApplicationState, 'navigation' | 'productsQuery'>,
): SyncProductsQueryFromURLPayload {
  const query = getQueryFromAsPath(asPath);

  const pathOnly = stripQueryAndHash(asPath);
  let urlFacets: Array<{ alias: string; values: string[] }> = [];
  const { surface, taxonomyAttributeAliases } = state.productsQuery;

  try {
    urlFacets = parse(pathOnly, {
      basePath: shopSurfaceBasePath(surface) ?? '',
      taxonomyAliases: taxonomyAttributeAliases,
    }).map(({ alias, values }) => ({ alias, values }));
  } catch (error) {
    if (!(error instanceof RouteParseError)) {
      throw error;
    }
  }

  // A facet can become single-select after links holding several of its values were shared.
  const singleSelectAliases = new Set(
    state.productsQuery.facets.filter((facet) => facet.type === 'single').map((f) => f.alias),
  );
  const activeFacetValuesByKey: Record<string, string[]> = {};
  urlFacets.forEach(({ alias, values }) => {
    activeFacetValuesByKey[alias] = singleSelectAliases.has(alias) ? values.slice(0, 1) : values;
  });

  const includeSearchTerm = state.navigation.pageType === PageType.Search;

  return {
    activeFacetValuesByKey,
    activeSort: parseSortValue(getFirstValue(query.sort)),
    page: getPageFromParam(query),
    searchTerm: includeSearchTerm ? getFirstValue(query.term) : undefined,
  };
}
